// @ts-check

/**
 * Cart drawer Web Component.
 * - Open/close with animation, backdrop, focus trap, scroll lock
 * - Quantity changes and item removal via Cart API
 * - Section Rendering API for content updates
 * - Auto-opens on cart:change events
 * - Product recommendations loaded via Recommendations API
 *
 * @attr aria-hidden - Controls visibility (true/false)
 */
(() => {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      // Close buttons (overlay + X button)
      this.querySelectorAll('[data-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      // Escape key
      this._onKeydown = (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onKeydown);

      // Click delegation for all button actions
      this.addEventListener('click', this._handleClick.bind(this));

      // Listen for cart:change events (from product-form.js)
      this._onCartChange = (/** @type {CustomEvent} */ e) => {
        this._refresh().then(() => {
          this.open();
        });
      };
      document.addEventListener('cart:change', this._onCartChange);

      // Listen for manual cart:open events (from header bag button)
      this._onCartOpen = () => {
        this.open();
      };
      document.addEventListener('cart:open', this._onCartOpen);

      // Load recommendations if drawer has items on initial render
      const recsContainer = this.querySelector('.cart-drawer__recs');
      if (recsContainer && recsContainer.dataset.productId) {
        this._loadRecommendations();
      }

      // Set up swipe-to-close gesture
      this._setupSwipeToClose();
    }

    disconnectedCallback() {
      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
      }
      if (this._onCartChange) {
        document.removeEventListener('cart:change', this._onCartChange);
      }
      if (this._onCartOpen) {
        document.removeEventListener('cart:open', this._onCartOpen);
      }
    }

    /**
     * Handle all clicks via delegation
     * @param {MouseEvent} e
     */
    _handleClick(e) {
      const target = /** @type {HTMLElement} */ (e.target);
      const button = target.closest('[data-action]');

      if (button) {
        const action = button.getAttribute('data-action');
        const lineKey = button.getAttribute('data-line-key');
        const quantity = button.getAttribute('data-quantity');
        const variantId = button.getAttribute('data-variant-id');

        if (action === 'increment' || action === 'decrement' || action === 'remove') {
          if (lineKey && quantity !== null) {
            this._updateItem(lineKey, parseInt(quantity, 10), button);
          }
        } else if (variantId) {
          this._quickAdd(variantId, button);
        }
        return;
      }

      // Close drawer when clicking product links (navigates to PDP)
      const link = target.closest('a[href]');
      if (link) {
        this.close();
      }
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this._previousFocus = /** @type {HTMLElement | null} */ (
        document.activeElement
      );

      // Focus close button after a frame (let transition start)
      requestAnimationFrame(() => {
        const closeButton = this.querySelector('[data-close]');
        if (closeButton) {
          /** @type {HTMLElement} */ (closeButton).focus();
        }
      });
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';

      // Return focus to previous element
      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    }

    /**
     * Update item quantity via Cart API
     * @param {string} key - Line item key
     * @param {number} quantity - New quantity (0 to remove)
     * @param {HTMLElement} trigger - The button that triggered the update
     */
    async _updateItem(key, quantity, trigger) {
      const lineItem = trigger.closest('.line-item');
      if (lineItem) {
        lineItem.setAttribute('aria-busy', 'true');
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) throw new Error('Failed to update cart');

        const cart = await response.json();

        // Refresh drawer content
        await this._refresh();

        // Update header count silently (without re-opening drawer)
        document.dispatchEvent(
          new CustomEvent('cart:change:silent', {
            detail: { count: cart.item_count },
          })
        );

        // Announce to screen readers
        const titleEl = lineItem?.querySelector('.line-item__title');
        const productName = titleEl?.textContent?.trim() || 'Item';
        if (quantity === 0) {
          this._announce(`${productName} removed from bag`);
        } else {
          this._announce(`${productName} quantity updated to ${quantity}`);
        }
      } catch (error) {
        console.error('Cart update error:', error);
        this._announce('Failed to update item');
      } finally {
        if (lineItem) {
          lineItem.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * Quick-add a product variant to cart.
     * Shows checkmark icon feedback for --duration-slow, then refreshes.
     * @param {string} variantId
     * @param {HTMLElement} button
     */
    async _quickAdd(variantId, button) {
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        // Swap to checkmark icon from Liquid-rendered template (same-origin)
        const checkTemplate = /** @type {HTMLTemplateElement | null} */ (
          document.getElementById('cart-drawer-check-icon')
        );
        if (checkTemplate) {
          button.textContent = '';
          button.appendChild(checkTemplate.content.cloneNode(true));
        }

        // Announce with product name
        const card = button.closest('.cart-recs__card');
        const cardTitle = card?.querySelector('.cart-recs__card-title');
        const addedName = cardTitle?.textContent?.trim() || 'Item';
        this._announce(`Added ${addedName} to bag`);

        // Read --duration-slow from CSS (respects prefers-reduced-motion: 0ms)
        const duration =
          parseInt(getComputedStyle(this).getPropertyValue('--duration-slow'), 10) || 500;

        // Show checkmark for the design-specified duration
        await new Promise((r) => setTimeout(r, duration));

        // Fetch full cart state for header count
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        document.dispatchEvent(
          new CustomEvent('cart:change:silent', {
            detail: { count: cart.item_count },
          })
        );

        // Section re-render replaces DOM (no manual cleanup needed)
        await this._refresh();
      } catch (error) {
        console.error('Quick-add error:', error);
        button.removeAttribute('aria-busy');
        const failCard = button.closest('.cart-recs__card');
        const failTitle = failCard?.querySelector('.cart-recs__card-title');
        const failName = failTitle?.textContent?.trim() || 'item';
        this._announce(`Failed to add ${failName}`);
      }
    }

    /**
     * Refresh drawer content via Section Rendering API.
     * HTML comes from same-origin Shopify Liquid rendering, safe for DOM replacement.
     */
    async _refresh() {
      try {
        const response = await fetch('/?sections=cart-drawer');
        if (!response.ok) throw new Error('Failed to fetch cart drawer');

        const data = await response.json();
        const html = data['cart-drawer'];

        // Parse the HTML from same-origin section render
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract new content
        const newBody = doc.querySelector('#cart-drawer-body');
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const newTitle = doc.querySelector('#cart-drawer-title');

        // Replace content (safe: HTML from same-origin Liquid rendering)
        const currentBody = this.querySelector('#cart-drawer-body');
        const currentFooter = this.querySelector('.cart-drawer__footer');
        const currentTitle = this.querySelector('#cart-drawer-title');

        if (newBody && currentBody) {
          currentBody.innerHTML = newBody.innerHTML;
        }
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
        if (newTitle && currentTitle) {
          currentTitle.textContent = newTitle.textContent;
        }

        // Load recommendations if items exist
        this._loadRecommendations();
      } catch (error) {
        console.error('Section Rendering error:', error);
      }
    }

    /**
     * Load product recommendations via Recommendations API
     */
    async _loadRecommendations() {
      const recsContainer = this.querySelector('.cart-drawer__recs');
      if (!recsContainer) return;

      const productId = recsContainer.dataset.productId;
      const limit = recsContainer.dataset.limit || '4';

      if (!productId) return;

      try {
        const response = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=${limit}&section_id=cart-drawer-recommendations`
        );

        if (!response.ok) return; // Silently fail - recommendations are non-critical

        const data = await response.json();

        // If we got HTML from the section endpoint, use it (safe: same-origin)
        if (data.sections && data.sections['cart-drawer-recommendations']) {
          recsContainer.innerHTML = data.sections['cart-drawer-recommendations'];
        }
      } catch (error) {
        console.error('Recommendations error:', error);
        // Silently fail - recommendations are non-critical
      }
    }

    /**
     * Announce message to screen readers
     * @param {string} message
     */
    _announce(message) {
      const status = this.querySelector('#cart-drawer-status');
      if (status) {
        status.textContent = message;
        setTimeout(() => {
          status.textContent = '';
        }, 3000);
      }
    }

    /**
     * Set up horizontal swipe-to-close on the drawer panel.
     * A right swipe past 100px threshold closes the drawer.
     */
    _setupSwipeToClose() {
      const panel = this.querySelector('.cart-drawer__panel');
      if (!panel) return;

      /** @type {number} */ let startX = 0;
      /** @type {number} */ let startY = 0;
      /** @type {boolean} */ let tracking = false;

      panel.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      panel.addEventListener(
        'touchmove',
        (e) => {
          if (!tracking) return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;

          // Only track rightward swipes with more horizontal than vertical movement
          if (dx > 0 && Math.abs(dx) > Math.abs(dy)) {
            /** @type {HTMLElement} */ (panel).style.transform = `translateX(${dx}px)`;
          } else {
            tracking = false;
            /** @type {HTMLElement} */ (panel).style.transform = '';
          }
        },
        { passive: true }
      );

      panel.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;

          const dx = e.changedTouches[0].clientX - startX;
          /** @type {HTMLElement} */ (panel).style.transform = '';

          if (dx > 100) {
            this.close();
          }
        },
        { passive: true }
      );
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
